import { CANNON, CREW, SHIP, WORLD } from "./config.ts";
import { BASTION, BASTION_BARREL } from "./bastion.ts";
import { CLIFF_HALF, CLIFF_SPACING, mesaContour, mesaEdge, mesaHeight, mesaPlateau, mesaShape, rampHeight, rampProjection, trailDistance, type Mesa, type Ramp, type Trail } from "./landforms.ts";
import { TAU, createRng, smoothstep, valueNoise, type Pose, type Vec3 } from "./math.ts";
import { LANTERN_ISLE, LANTERN_SOLIDS, lanternTerrainHeight } from "./lantern.ts";
import { SKULL_ISLE, SKULL_PALM_COLLIDERS, SKULL_SOLIDS, skullCoastRadius, skullTerrainHeight } from "./skull.ts";

/**
 * World data shared by client (rendering) and server (collision, quests).
 * Islands are analytic heightfields: client meshes and server collision sample
 * the same `terrainHeight` function, so they always agree.
 */

export const ISLAND_IDS = ["lantern", "crescent", "twinback", "smuggler", "coral", "mistral", "skull", "farwatch", "bastion"] as const;
export type IslandId = typeof ISLAND_IDS[number];
export type LandmassId = IslandId | "outpost" | "ember" | "frostgate" | "mirewatch";
export type IslandTheme = "tropical" | "volcanic" | "frost" | "marsh" | "ruins";

export interface Harmonic {
  k: number;
  amp: number;
  phase: number;
}

/** Gaussian-like bump (positive) or hollow (negative) in island-local metres. */
export interface Bump {
  x: number;
  z: number;
  r: number;
  h: number;
}

export type LandmarkKind = "lighthouse" | "windmill" | "palmGrove" | "twinPeaks" | "arch" | "shipwreck" | "bigRock" | "hut" | "volcano" | "obsidianSpire" | "iceSpire" | "pineGrove" | "deadTree" | "stiltHut" | "pillars" | "statue"
  | "beaconTower" | "seaLookout" | "stockadeGate";

export interface Landmark {
  id: string;
  kind: LandmarkKind;
  label: string;
  /** Island-local position. */
  x: number;
  z: number;
  /** Model yaw (radians); defaults to 0. */
  yaw?: number;
}

/** Hand-placed scenery models with shared collision; names match client scenery models. */
export type PropModel = "beaconTower" | "seaLookout" | "stockadeGate" | "palisade" | "stakes" | "stakesWall"
  | "scaffoldTower" | "fortShack" | "crateStack" | "bannerPole" | "campfire"
  | "gateLever" | "lootCrate" | "mountedTelescope";

export interface Prop {
  model: PropModel;
  /** Island-local position, or local to the `on` model's frame. */
  x: number;
  z: number;
  yaw: number;
  /** Absolute base height (island props) or height above the `on` model's base. */
  y?: number;
  /** Optional handle so other props can stand on this one. */
  id?: string;
  /** Landmark or prop id whose model frame this prop is placed in (towers, decks). */
  on?: string;
}

/** World placement of a model root. */
export interface Placement {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

/** Straight timber stair along one world axis, rising from (x, z) at `base` to a landing at `top`. */
export interface Stair {
  /** Island-local centre of the bottom edge. */
  x: number;
  z: number;
  /** Climb direction: exactly one of dirX/dirZ is ±1. */
  dirX: number;
  dirZ: number;
  width: number;
  base: number;
  top: number;
  steps: number;
  tread: number;
  /** Flat landing length after the last step. */
  landing: number;
}

/** Fallen cliff block; island-local. */
export interface Boulder {
  x: number;
  z: number;
  r: number;
  h: number;
  yaw: number;
}

/** Plank jetty on piles; island-local extents. */
export interface Dock {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
  top: number;
}

export interface DigSpot {
  id: string;
  /** Island-local position. */
  x: number;
  z: number;
  /** Landmark the treasure map uses as a reference. */
  landmark: string;
  hint: string;
}

export interface Landmass {
  id: LandmassId;
  name: string;
  kind: "island" | "outpost";
  theme: IslandTheme;
  cx: number;
  cz: number;
  radius: number;
  plateau: number;
  peak: number;
  seed: number;
  harmonics: readonly Harmonic[];
  bumps: readonly Bump[];
  landmarks: readonly Landmark[];
  digSpots: readonly DigSpot[];
  /** Theme-specific tree positions, island-local. */
  trees: readonly { x: number; z: number }[];
  /** Height of the coastline itself (default 0.4); below sea level the island is a drowned shelf. */
  shore?: number;
  /** Cliff-walled plateaus; their walls get shared collider boxes. */
  mesas?: readonly Mesa[];
  /** Footpaths worn into the ground (rendering only). */
  trails?: readonly Trail[];
  ramps?: readonly Ramp[];
  stairs?: readonly Stair[];
  docks?: readonly Dock[];
  boulders?: readonly Boulder[];
  props?: readonly Prop[];
}

export interface Island extends Landmass {
  id: IslandId;
}

const originalIslands: readonly Island[] = [
  LANTERN_ISLE,
  {
    id: "crescent",
    name: "Crescent Cay",
    kind: "island",
    theme: "tropical",
    cx: 700,
    cz: -150,
    radius: 96,
    plateau: 1.3,
    peak: 6,
    seed: 37,
    harmonics: [
      { k: 1, amp: 0.18, phase: 1.2 },
      { k: 2, amp: 0.1, phase: 2.6 },
      { k: 4, amp: 0.04, phase: 0.2 },
    ],
    bumps: [
      { x: -38, z: 30, r: 26, h: 5 },
      { x: 52, z: -12, r: 40, h: -14 },
    ],
    landmarks: [
      { id: "crescent-windmill", kind: "windmill", label: "Windmill", x: -38, z: 30 },
      { id: "crescent-grove", kind: "palmGrove", label: "Palm Grove", x: -30, z: -40 },
      { id: "crescent-hut", kind: "hut", label: "Fisher's Hut", x: 5, z: 62 },
    ],
    digSpots: [
      { id: "crescent-1", x: -28, z: 18, landmark: "crescent-windmill", hint: "South-east of the windmill." },
      { id: "crescent-2", x: -40, z: -48, landmark: "crescent-grove", hint: "Inside the palm grove." },
      { id: "crescent-3", x: 12, z: 52, landmark: "crescent-hut", hint: "Behind the fisher's hut." },
    ],
    trees: [
      { x: -30, z: -40 },
      { x: -22, z: -46 },
      { x: -36, z: -32 },
      { x: -44, z: -46 },
      { x: -26, z: -54 },
      { x: -60, z: 0 },
      { x: 20, z: 40 },
    ],
  },
  {
    id: "twinback",
    name: "Twinback Rock",
    kind: "island",
    theme: "tropical",
    cx: 60,
    cz: 420,
    radius: 90,
    plateau: 1.5,
    peak: 4,
    seed: 53,
    harmonics: [
      { k: 2, amp: 0.15, phase: 0.0 },
      { k: 3, amp: 0.06, phase: 1.4 },
      { k: 6, amp: 0.03, phase: 2.1 },
    ],
    bumps: [
      { x: -30, z: 20, r: 26, h: 16 },
      { x: 28, z: 24, r: 24, h: 14 },
    ],
    landmarks: [
      { id: "twinback-west", kind: "twinPeaks", label: "West Peak", x: -30, z: 20 },
      { id: "twinback-east", kind: "twinPeaks", label: "East Peak", x: 28, z: 24 },
      { id: "twinback-arch", kind: "arch", label: "Stone Arch", x: 0, z: -48 },
    ],
    digSpots: [
      { id: "twinback-1", x: 0, z: -12, landmark: "twinback-arch", hint: "Between the peaks, north of the arch." },
      { id: "twinback-2", x: -2, z: -40, landmark: "twinback-arch", hint: "Just past the stone arch." },
      { id: "twinback-3", x: 52, z: -8, landmark: "twinback-east", hint: "On the beach below the east peak." },
    ],
    trees: [
      { x: -50, z: -20 },
      { x: -45, z: -30 },
      { x: 40, z: -35 },
      { x: 20, z: -40 },
      { x: -20, z: -55 },
    ],
  },
];

export interface Outpost {
  id: Exclude<LandmassId, IslandId>;
  name: string;
  landmass: Landmass;
  pier: { x0: number; x1: number; z0: number; z1: number; top: number };
  berths: readonly Pose[];
  /** Pier-top landings bridging the berth gap to each berth's waist gangway (`berths[i]` ↔ `gangways[i]`). */
  gangways: readonly { minX: number; maxX: number; minZ: number; maxZ: number }[];
  questBoard: Vec3;
  merchant: Vec3;
  merchantStand: { x: number; z: number };
  merchantLanding: { minX: number; maxX: number; minZ: number; maxZ: number };
  spawns: readonly { x: number; z: number }[];
  buildings: readonly { minX: number; maxX: number; minZ: number; maxZ: number; h: number }[];
}

/** Water between the pier edge and a berthed hull's side, so a departing ship can put its helm over without its stern grinding on the piles. */
export const BERTH_GAP = 1;
/** Berth rows along the pier, centre to centre: the bow-to-stern room lets a ship bear away from behind the one moored ahead. */
export const BERTH_SPACING = 36;
/** Half-width of a gangway landing: the waist rails leave a 2 m gap amidships. */
const GANGWAY_HALF = 0.8;

function makeOutpost(id: Outpost["id"], name: string, theme: IslandTheme, cx: number, cz: number, seed: number): Outpost {
  const pier = { x0: cx - 2, x1: cx + 2, z0: cz + 44, z1: cz + 74 + 3 * BERTH_SPACING + 14, top: SHIP.deckHeight };
  const merchant = { x: cx - 1.2, y: pier.top, z: cz + 58 };
  // Pairs either side of the pier, bows out to sea, a berth gap off the pier edge.
  const berths = Array.from({ length: 8 }, (_, i) => ({
    x: cx + (i % 2 === 0 ? 1 : -1) * (2 + BERTH_GAP + SHIP.beam / 2), z: cz + 74 + Math.floor(i / 2) * BERTH_SPACING, heading: 0,
  }));
  return {
    id, name,
    landmass: {
      id, name, kind: "outpost", theme, cx, cz, radius: 52, plateau: 1.2, peak: 5, seed,
      harmonics: [{ k: 2, amp: .08, phase: .4 }, { k: 3, amp: .05, phase: 1.9 }],
      bumps: [{ x: -20, z: -18, r: 18, h: 3 }], landmarks: [], digSpots: [],
      trees: theme === "volcanic" ? [] : [{ x: -30, z: 10 }, { x: -26, z: 18 }, { x: 24, z: -20 }, { x: 30, z: -8 }, { x: -10, z: -34 }],
    },
    pier,
    berths,
    gangways: berths.map((b) => {
      // From the pier edge to just inside the deck edge, so the walk across never leaves a support.
      const side = Math.sign(b.x - cx), pierEdge = side > 0 ? pier.x1 : pier.x0, deckEdge = b.x - side * (SHIP.beam / 2 - 0.05);
      return { minX: Math.min(pierEdge, deckEdge), maxX: Math.max(pierEdge, deckEdge), minZ: b.z - GANGWAY_HALF, maxZ: b.z + GANGWAY_HALF };
    }),
    questBoard: { x: cx - 1.2, y: pier.top, z: cz + 50 },
    merchant, merchantStand: { x: merchant.x - 1, z: merchant.z },
    merchantLanding: { minX: pier.x0 - 1, maxX: pier.x0, minZ: merchant.z - 1.6, maxZ: merchant.z + 1.6 },
    spawns: [{ x: cx, z: cz + 64 }, { x: cx - .8, z: cz + 61 }, { x: cx + .8, z: cz + 61 }, { x: cx, z: cz + 58 }],
    buildings: [{ minX: cx - 22, maxX: cx - 8, minZ: cz + 8, maxZ: cz + 20, h: 7 }, { minX: cx + 8, maxX: cx + 18, minZ: cz + 4, maxZ: cz + 14, h: 6 }],
  };
}

export const OUTPOSTS: readonly Outpost[] = [
  makeOutpost("outpost", "Driftwood Landing", "tropical", 0, -1000, 11),
  makeOutpost("ember", "Ember Wharf", "volcanic", 2400, -1800, 12),
  makeOutpost("frostgate", "Frostgate", "frost", -2000, 2100, 13),
  makeOutpost("mirewatch", "Mirewatch", "marsh", 2100, 1800, 14),
];
export function homeOutpost(slot: number): Outpost { return OUTPOSTS[slot % OUTPOSTS.length]!; }
export const BERTHS: readonly Pose[] = Array.from({ length: CREW.maxCrews }, (_, slot) => homeOutpost(slot).berths[Math.floor(slot / 4)]!);
export function nearestOutpost(x: number, z: number): Outpost {
  let nearest = OUTPOSTS[0]!, best = Infinity;
  for (const o of OUTPOSTS) {
    const d = (x - o.landmass.cx) ** 2 + (z - o.landmass.cz) ** 2;
    if (d < best) { best = d; nearest = o; }
  }
  return nearest;
}

type IslandRow = { id: IslandId; name: string; theme: IslandTheme; cx: number; cz: number; radius: number; seed: number };
const polar = (a: number, d: number) => ({ x: Math.cos(a) * d, z: Math.sin(a) * d });
function compassHint(m: Landmark, p: { x: number; z: number }): string {
  const directions = ["East", "North-east", "North", "North-west", "West", "South-west", "South", "South-east"];
  const i = (Math.round(Math.atan2(p.z - m.z, p.x - m.x) / (Math.PI / 4)) + 8) % 8;
  return `${directions[i]} of the ${m.label}.`;
}
function makeIsland(row: IslandRow): Island {
  const r = createRng(row.seed), R = row.radius, theme = row.theme;
  const harmonics = [{ k: 2, amp: .08 + .07 * r(), phase: r() * TAU }, { k: 3, amp: .04 + .04 * r(), phase: r() * TAU }, { k: 5, amp: .02 + .02 * r(), phase: r() * TAU }];
  const a0 = r() * TAU;
  const peaks = { tropical: 6, volcanic: 7, frost: 8, marsh: 2.5, ruins: 5 };
  const bumps: Bump[] = theme === "tropical" ? [{ ...polar(a0, .5 * R), r: .3 * R, h: 7 }]
    : theme === "volcanic" ? [{ x: 0, z: 0, r: .45 * R, h: 14 }, { x: 0, z: 0, r: .15 * R, h: -6 }]
    : theme === "frost" ? [{ x: 0, z: 0, r: .35 * R, h: 9 }]
    : theme === "marsh" ? [{ ...polar(a0 + Math.PI, .2 * R), r: .3 * R, h: -1.2 }]
    : [{ x: 0, z: 0, r: .3 * R, h: 4 }];
  const kinds: Record<IslandTheme, readonly LandmarkKind[]> = { tropical: ["lighthouse", "hut", "shipwreck"], volcanic: ["volcano", "obsidianSpire", "bigRock"], frost: ["iceSpire", "pineGrove", "hut"], marsh: ["deadTree", "stiltHut", "shipwreck"], ruins: ["pillars", "statue", "arch"] };
  const labels: Partial<Record<LandmarkKind, string>> = { lighthouse: "Lighthouse", hut: theme === "frost" ? "Trapper's Cabin" : "Fisher's Hut", shipwreck: "Old Wreck", volcano: "Smoking Cone", obsidianSpire: "Obsidian Spire", bigRock: "Black Rock", iceSpire: "Ice Spire", pineGrove: "Pine Grove", deadTree: "Hanging Tree", stiltHut: "Stilt Hut", pillars: "Old Colonnade", statue: "Weathered Statue", arch: "Stone Arch" };
  const landmarks = kinds[theme].map((kind, i) => ({ id: `${row.id}-${kind}`, kind, label: labels[kind]!, ...(kind === "volcano" ? { x: 0, z: 0 } : polar(a0 + i * TAU / 3, .5 * R)) }));
  const digSpots = landmarks.map((m, i) => {
    const p = polar(a0 + i * TAU / 3 + .45, .6 * R);
    return { id: `${row.id}-${i + 1}`, ...p, landmark: m.id, hint: compassHint(m, p) };
  });
  const trees: { x: number; z: number }[] = [];
  const count = { tropical: 6, frost: 8, marsh: 5, ruins: 4, volcanic: 0 }[theme];
  for (let i = 0; i < count; i++) {
    const p = polar(r() * TAU, (.25 + .5 * r()) * R);
    if (![...landmarks, ...digSpots].some((q) => Math.hypot(q.x - p.x, q.z - p.z) < 8)) trees.push(p);
  }
  const grove = landmarks.find((m) => m.kind === "pineGrove");
  if (grove) for (let i = 0; i < 5; i++) {
    const p = polar(i * TAU / 5, 6);
    trees.push({ x: grove.x + p.x, z: grove.z + p.z });
  }
  return { ...row, kind: "island", plateau: 1.4, peak: peaks[theme], harmonics, bumps, landmarks, digSpots, trees };
}
const generatedIslands: readonly Island[] = [
  { id: "smuggler", name: "Smuggler's Den", theme: "marsh", cx: -1900, cz: -1500, radius: 88, seed: 61 },
  { id: "coral", name: "Coral Spire", theme: "volcanic", cx: 1800, cz: -1900, radius: 80, seed: 71 },
  { id: "mistral", name: "Mistral Atoll", theme: "tropical", cx: 2100, cz: 900, radius: 100, seed: 83 },
  SKULL_ISLE,
  { id: "farwatch", name: "Farwatch Isle", theme: "frost", cx: 300, cz: 2300, radius: 86, seed: 109 },
].map((row) => row.id === "skull" ? SKULL_ISLE : makeIsland(row as IslandRow));
export const LANDMASSES: readonly Landmass[] = [...OUTPOSTS.map((o) => o.landmass), ...originalIslands, ...generatedIslands, BASTION];
export const ISLANDS = LANDMASSES.filter((l): l is Island => l.kind === "island");
if (ISLANDS.length < 3) throw new Error("treasure hunts require at least three islands");
const ISLAND_BY_ID = new Map<string, Island>(ISLANDS.map((i) => [i.id, i]));
export function isIslandId(v: unknown): v is IslandId { return typeof v === "string" && ISLAND_BY_ID.has(v); }
export function getIsland(id: IslandId): Island {
  const island = ISLAND_BY_ID.get(id);
  if (!island) throw new Error(`unknown island ${id}`);
  return island;
}
export function islandsNear(x: number, z: number, n: number): Island[] {
  return [...ISLANDS].sort((a, b) => ((a.cx - x) ** 2 + (a.cz - z) ** 2) - ((b.cx - x) ** 2 + (b.cz - z) ** 2)).slice(0, n);
}
export const NPC_SPAWNS: readonly Pose[] = [
  [-2400, -2400], [2400, -2400], [2600, 0], [-2600, 0], [-2400, 2400], [2400, 2400], [0, -2600], [1000, 2600],
].map(([x, z]) => ({ x: x!, z: z!, heading: 0 }));
export const NPC_WAYPOINTS: readonly { x: number; z: number }[] = [
  [-1200, -2000], [1200, -2200], [2400, -800], [2500, 1600], [1200, 1500], [-300, 1500], [-2500, 600], [-2300, -800], [-1000, 600], [1300, 500], [-1100, -1200], [1100, -1100],
].map(([x, z]) => ({ x: x!, z: z! }));


/** Coastline radius of a landmass in a given direction (where height ≈ plateau base). */
export function coastRadius(l: Landmass, theta: number): number {
  if (l.id === "skull") return skullCoastRadius(theta);
  let f = 1;
  for (const h of l.harmonics) f += h.amp * Math.sin(h.k * theta + h.phase);
  return l.radius * f;
}

function landmassHeight(l: Landmass, x: number, z: number): number {
  const dx = x - l.cx;
  const dz = z - l.cz;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d > l.radius * 2.2) return WORLD.seaFloor;
  if (l.id === "skull") return skullTerrainHeight(dx, dz, WORLD.seaFloor);
  const s = d / coastRadius(l, Math.atan2(dz, dx));
  let h: number;
  const shore = l.shore ?? 0.4;
  if (s >= 1) {
    // Shelf to -3 m by s = 1.18, then down to the sea floor.
    const shelf = shore + (-3 - shore) * smoothstep(1, 1.18, s);
    h = s < 1.18 ? shelf : -3 + (WORLD.seaFloor + 3) * smoothstep(1.18, 1.7, s);
  } else {
    const inland = 1 - smoothstep(0.82, 1, s);
    h = shore + (l.plateau - shore) * inland + (l.peak - l.plateau) * (1 - smoothstep(0.1, 0.8, s));
    h += valueNoise(x * 0.06, z * 0.06, l.seed) * 0.8 * inland;
  }
  for (const b of l.bumps) {
    const bx = dx - b.x;
    const bz = dz - b.z;
    const q = 1 - (bx * bx + bz * bz) / (b.r * b.r);
    if (q > 0) h += b.h * q * q;
  }
  if (l.mesas) for (const m of l.mesas) h = Math.max(h, mesaHeight(m, dx, dz));
  if (l.ramps) for (const r of l.ramps) h = Math.max(h, rampHeight(r, dx, dz));
  return l.id === "lantern" ? lanternTerrainHeight(dx, dz, h) : h;
}

/** Terrain height (y) at a world position; open sea returns the sea floor. */
export function terrainHeight(x: number, z: number): number {
  let h: number = WORLD.seaFloor;
  for (const l of LANDMASSES) {
    const v = landmassHeight(l, x, z);
    if (v > h) h = v;
  }
  return h;
}

/** Trampled yard or worn footpath at island-local (lx, lz); `fray` widens paths (rendered edge noise). */
export function isTrodden(l: Landmass, lx: number, lz: number, fray = 0): boolean {
  return (l.mesas?.some((m) => m.sandy && mesaShape(m, lx, lz) < 1) ?? false)
    || (l.trails?.some((t) => trailDistance(t, lx, lz) < t.width / 2 + fray) ?? false);
}

/** An outpost building's footprint in the world frame. */
export interface Footprint { readonly minX: number; readonly maxX: number; readonly minZ: number; readonly maxZ: number; }

/** Where an outpost building sits on the terrain under its footprint. */
export interface BuildingSeat {
  /** Height of the building's own floor. */
  readonly floor: number;
  /** Underside of the model's own rock footing. */
  readonly top: number;
  /** Bottom of the rendered foundation, and therefore of its collider. */
  readonly base: number;
}

/**
 * Seats an outpost building on the terrain beneath it. The floor is the highest
 * sample under the footprint, so no wall is buried in the hillside; the model's
 * footing stops 0.55 m lower, and the foundation continues 0.6 m below the
 * lowest ground so the downhill side is closed. World data and the renderer both
 * use this, so the collider and the rendered foundation never disagree.
 */
export function outpostBuildingSeat(b: Footprint): BuildingSeat {
  let low = Infinity;
  let high = -Infinity;
  for (let i = 0; i <= 8; i++) {
    const x = b.minX + ((b.maxX - b.minX) * i) / 8;
    for (let k = 0; k <= 8; k++) {
      const z = b.minZ + ((b.maxZ - b.minZ) * k) / 8;
      const h = terrainHeight(x, z);
      if (h < low) low = h;
      if (h > high) high = h;
    }
  }
  const top = high - 0.55;
  return { floor: high, top, base: Math.min(low, top) - 0.6 };
}

/** Landmass whose coastline area (with a margin) contains the point. */
export function landmassAt(x: number, z: number, margin = 1.25): Landmass | undefined {
  for (const l of LANDMASSES) {
    const dx = x - l.cx;
    const dz = z - l.cz;
    if (Math.sqrt(dx * dx + dz * dz) <= coastRadius(l, Math.atan2(dz, dx)) * margin) return l;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Static colliders (world frame)

export interface CylCollider {
  kind: "cyl";
  x: number;
  z: number;
  r: number;
  y0: number;
  y1: number;
  /** Ships collide with this shape too (rocks, pier piles). */
  blocksShip: boolean;
}

export interface BoxCollider {
  kind: "box";
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  y0: number;
  y1: number;
  blocksShip: boolean;
}

export type Collider = CylCollider | BoxCollider;

/** Piles sit under the pier deck, clear of a berthed hull. */
export const PILE_INSET = 0.6;

export const DOCK_ZONE_RADIUS = 18;


/** One exposed stretch of mesa wall (world frame). */
export interface CliffColumn {
  x: number;
  z: number;
  /** Outward unit normal. */
  nx: number;
  nz: number;
  /** Ground (or sea bed) just outside the wall. */
  base: number;
  /** Walkable plateau height at the cliff edge. */
  top: number;
}

/** Exposed cliff walls: skips walls buried in a higher tier, flush with lower ground, or cut by a ramp. */
export function cliffColumns(l: Landmass): CliffColumn[] {
  const out: CliffColumn[] = [];
  for (const m of l.mesas ?? []) {
    const top = mesaPlateau(m, 1 - mesaEdge(m));
    const reach = m.edge / 2 + 2.5;
    for (const p of mesaContour(m, 0, CLIFF_SPACING)) {
      const x = l.cx + p.x, z = l.cz + p.z;
      if (landmassHeight(l, x, z) > top + .6) continue;
      const base = landmassHeight(l, x + p.nx * reach, z + p.nz * reach);
      if (base > top - 1.2) continue;
      if (l.ramps?.some((r) => {
        const q = rampProjection(r, p.x, p.z);
        return q.side < r.width / 2 + 1 && q.beyond < 1;
      })) continue;
      out.push({ x, z, nx: p.nx, nz: p.nz, base, top });
    }
  }
  return out;
}

type PropShape =
  | { kind: "cyl"; x: number; z: number; r: number; h: number; y0?: number; blocksShip?: boolean }
  | { kind: "box"; x?: number; z?: number; hx: number; hz: number; h: number; y0?: number };

const corners = (d: number, r: number, h: number, blocksShip = false): PropShape[] =>
  [[-d, -d], [d, -d], [-d, d], [d, d]].map(([x, z]) => ({ kind: "cyl", x: x!, z: z!, r, h, blocksShip }));

/**
 * A plank deck at height `y` plus rails on the back and both sides; the front (-z) stays
 * open where the ladder arrives.
 */
const deck = (half: number, y: number, rail: number): PropShape[] => [
  { kind: "box", hx: half, hz: half, y0: y - .24, h: .24 },
  { kind: "box", z: rail, hx: rail, hz: .07, y0: y, h: 1.05 },
  { kind: "box", x: -rail, hx: .07, hz: rail, y0: y, h: 1.05 },
  { kind: "box", x: rail, hx: .07, hz: rail, y0: y, h: 1.05 },
];

/** Model-local collision (x right, z forward), matching tools/models/bastion.py. */
const PROP_SHAPES: Record<PropModel, readonly PropShape[]> = {
  beaconTower: [...corners(2, .25, 16), ...deck(2.5, 16, 2.4), { kind: "cyl", x: 0, z: 0, r: .2, y0: 16, h: 8.6 }],
  seaLookout: [
    ...corners(2.88, .4, 5.2, true), ...deck(2.75, 11.5, 2.65),
    // Half-walled hut: left wall, the stub of the collapsed right wall, back wall, posts.
    { kind: "box", x: -1.75, hx: .06, hz: 1.5, y0: 11.5, h: 1.65 },
    { kind: "box", x: 1.75, z: .75, hx: .06, hz: .75, y0: 11.5, h: 1.2 },
    { kind: "box", x: .1, z: 1.72, hx: 1.75, hz: .06, y0: 11.5, h: 2.35 },
    ...[[-1.8, -1.65], [1.8, -1.65]].map(([x, z]) => ({ kind: "cyl" as const, x: x!, z: z!, r: .1, y0: 11.5, h: 2.95 })),
    { kind: "cyl", x: -1, z: 0, r: .32, y0: 11.5, h: .85 },
    { kind: "cyl", x: 2.2, z: .4, r: .13, y0: 11.5, h: 2.7 },
  ],
  stockadeGate: [{ kind: "cyl", x: -2.1, z: 0, r: .35, h: 6 }, { kind: "cyl", x: 2.1, z: 0, r: .35, h: 6 }],
  palisade: [-1.5, -.5, .5, 1.5].map((x) => ({ kind: "cyl", x, z: 0, r: .5, h: 3.4 })),
  scaffoldTower: [...corners(1.4, .2, 9), ...deck(1.8, 7, 1.7)],
  fortShack: [{ kind: "box", hx: 2.2, hz: 1.8, h: 3.4 }],
  crateStack: [{ kind: "box", hx: .8, hz: .6, h: 1.7 }],
  bannerPole: [{ kind: "cyl", x: 0, z: 0, r: .12, h: 6 }],
  gateLever: [{ kind: "cyl", x: 0, z: 0, r: .22, h: 1.3 }],
  mountedTelescope: [{ kind: "cyl", x: 0, z: 0, r: .25, h: 1.35 }],
  // Intact crates collide through the dynamic set (interactives.ts).
  lootCrate: [],
  stakes: [],
  stakesWall: [],
  campfire: [],
};

/** Babylon's yaw convention: local +z turns toward world +x. */
function propColliders(model: PropModel, at: Placement, out: Collider[]): void {
  const c = Math.cos(at.yaw), s = Math.sin(at.yaw);
  for (const shape of PROP_SHAPES[model]) {
    const lx = shape.x ?? 0, lz = shape.z ?? 0;
    const x = at.x + lx * c + lz * s, z = at.z - lx * s + lz * c;
    // Shapes resting on the ground sink half a metre so slopes never open a gap beneath them.
    const y0 = shape.y0 === undefined ? at.y - .5 : at.y + shape.y0, y1 = at.y + (shape.y0 ?? 0) + shape.h;
    if (shape.kind === "cyl") {
      out.push({ kind: "cyl", x, z, r: shape.r, y0, y1, blocksShip: shape.blocksShip ?? false });
      continue;
    }
    const quarter = at.yaw / (Math.PI / 2);
    if (Math.abs(quarter - Math.round(quarter)) > 1e-6) throw new Error(`${model} needs a quarter-turn yaw`);
    const [hx, hz] = Math.abs(Math.round(quarter)) % 2 ? [shape.hz, shape.hx] : [shape.hx, shape.hz];
    out.push({ kind: "box", minX: x - hx, maxX: x + hx, minZ: z - hz, maxZ: z + hz, y0, y1, blocksShip: false });
  }
}

/** Model-local (x, y, z) inside a placed model → world point. */
export function placementPoint(at: Placement, x: number, y: number, z: number): Vec3 {
  const c = Math.cos(at.yaw), s = Math.sin(at.yaw);
  return { x: at.x + x * c + z * s, y: at.y + y, z: at.z - x * s + z * c };
}

/** World placement of a prop, resolving props mounted on landmarks or other props. */
export function propPlacement(l: Landmass, p: Prop): Placement {
  if (!p.on) {
    const x = l.cx + p.x, z = l.cz + p.z;
    return { x, y: p.y ?? terrainHeight(x, z), z, yaw: p.yaw };
  }
  const parent = anchorPlacement(l, p.on);
  return { ...placementPoint(parent, p.x, p.y ?? 0, p.z), yaw: parent.yaw + p.yaw };
}

/** Placement of a landmark (by id) or an `id`-tagged prop. */
export function anchorPlacement(l: Landmass, id: string): Placement {
  const m = l.landmarks.find((q) => q.id === id);
  if (m) {
    const x = l.cx + m.x, z = l.cz + m.z;
    return { x, y: terrainHeight(x, z), z, yaw: m.yaw ?? 0 };
  }
  const p = l.props?.find((q) => q.id === id);
  if (!p) throw new Error(`unknown anchor ${id} on ${l.id}`);
  return propPlacement(l, p);
}

/** World-frame bounds of one stair tread (or the landing) between `a` and `b` metres along the climb. */
export function stairSpan(l: Landmass, s: Stair, a: number, b: number): { minX: number; maxX: number; minZ: number; maxZ: number } {
  const x0 = l.cx + s.x + s.dirX * a, x1 = l.cx + s.x + s.dirX * b;
  const z0 = l.cz + s.z + s.dirZ * a, z1 = l.cz + s.z + s.dirZ * b;
  const wx = s.dirX ? 0 : s.width / 2, wz = s.dirZ ? 0 : s.width / 2;
  return { minX: Math.min(x0, x1) - wx, maxX: Math.max(x0, x1) + wx, minZ: Math.min(z0, z1) - wz, maxZ: Math.max(z0, z1) + wz };
}

/** Pile positions under a dock, both long edges, every 6 m. */
export function dockPiles(l: Landmass, d: Dock): { x: number; z: number }[] {
  const piles: { x: number; z: number }[] = [];
  const alongX = d.x1 - d.x0 > d.z1 - d.z0;
  const [a0, a1] = alongX ? [d.x0, d.x1] : [d.z0, d.z1];
  for (let a = a0 + 1; a <= a1 - .5; a += 6) {
    for (const side of alongX ? [d.z0 + PILE_INSET, d.z1 - PILE_INSET] : [d.x0 + PILE_INSET, d.x1 - PILE_INSET]) {
      piles.push(alongX ? { x: l.cx + a, z: l.cz + side } : { x: l.cx + side, z: l.cz + a });
    }
  }
  return piles;
}

function buildIslandStructures(l: Landmass, out: Collider[]): void {
  for (const c of cliffColumns(l)) {
    out.push({ kind: "box", minX: c.x - CLIFF_HALF, maxX: c.x + CLIFF_HALF, minZ: c.z - CLIFF_HALF, maxZ: c.z + CLIFF_HALF,
      y0: Math.min(c.base, 0) - 2, y1: c.top, blocksShip: false });
  }
  for (const s of l.stairs ?? []) {
    const rise = (s.top - s.base) / s.steps;
    for (let i = 0; i <= s.steps; i++) {
      const a = i * s.tread, b = i === s.steps ? a + s.landing : a + s.tread;
      out.push({ kind: "box", ...stairSpan(l, s, a, b), y0: s.base - 1, y1: Math.min(s.top, s.base + rise * (i + 1)), blocksShip: false });
    }
  }
  for (const d of l.docks ?? []) {
    out.push({ kind: "box", minX: l.cx + d.x0, maxX: l.cx + d.x1, minZ: l.cz + d.z0, maxZ: l.cz + d.z1, y0: d.top - .4, y1: d.top, blocksShip: false });
    for (const p of dockPiles(l, d)) out.push({ kind: "cyl", ...p, r: .3, y0: WORLD.seaFloor, y1: d.top - .4, blocksShip: true });
  }
  for (const b of l.boulders ?? []) {
    const x = l.cx + b.x, z = l.cz + b.z;
    out.push({ kind: "cyl", x, z, r: b.r * .8, y0: WORLD.seaFloor, y1: b.h, blocksShip: terrainHeight(x, z) < -1 });
  }
  for (const p of l.props ?? []) propColliders(p.model, propPlacement(l, p), out);
}

/** Island supply barrels (island-local positions, on the side facing the outpost). */
export const SUPPLY_BARRELS: readonly { id: number; island: IslandId; x: number; z: number }[] = [
  { id: 0, island: "lantern", x: 33, z: -37 },
  { id: 1, island: "crescent", x: -31, z: -38 },
  { id: 2, island: "twinback", x: -3, z: -63 },
  ...generatedIslands.map((l, i) => {
    if (l.id === "skull") return { id: i + 3, island: l.id, x: -43.5, z: -98 };
    const o = nearestOutpost(l.cx, l.cz).landmass;
    const theta = Math.atan2(o.cz - l.cz, o.cx - l.cx);
    return { id: i + 3, island: l.id, ...polar(theta, .62 * coastRadius(l, theta)) };
  }),
  { id: 8, island: "bastion", ...BASTION_BARREL },
];

/** World position of a supply barrel's base, on the terrain. */
export function supplyBarrelWorld(id: number): { x: number; y: number; z: number } {
  const b = SUPPLY_BARRELS[id];
  if (!b) throw new Error(`bad supply barrel ${id}`);
  const l = LANDMASSES.find((m) => m.id === b.island);
  if (!l) throw new Error(`unknown island ${b.island}`);
  const x = l.cx + b.x;
  const z = l.cz + b.z;
  return { x, y: terrainHeight(x, z), z };
}

export const ROCKS: readonly { x: number; z: number; r: number; h: number }[] = [
  { x: -260, z: -720, r: 9, h: 9 },
  { x: 330, z: -640, r: 11, h: 12 },
  { x: -380, z: 40, r: 8, h: 8 },
  { x: 380, z: 160, r: 10, h: 10 },
  { x: -120, z: -420, r: 7, h: 7 },
  { x: 240, z: 260, r: 9, h: 11 },
  { x: -1500, z: -500, r: 9, h: 10 }, { x: 1500, z: -600, r: 10, h: 9 },
  { x: -800, z: -1800, r: 8, h: 8 }, { x: 700, z: -1700, r: 11, h: 12 },
  { x: 2200, z: -1300, r: 9, h: 10 }, { x: -2200, z: 1900, r: 10, h: 11 },
  { x: 1800, z: 2000, r: 9, h: 9 }, { x: -900, z: 2100, r: 8, h: 9 },
  { x: 2600, z: 300, r: 10, h: 10 }, { x: -2600, z: -1100, r: 9, h: 9 },
];

function buildColliders(includeBarrels = true): Collider[] {
  const out: Collider[] = [];
  for (const o of OUTPOSTS) {
    const p = o.pier, board = o.questBoard, merchant = o.merchant;
    out.push({ kind: "box", minX: p.x0, maxX: p.x1, minZ: p.z0, maxZ: p.z1, y0: p.top - .4, y1: p.top, blocksShip: false });
    for (const g of o.gangways) out.push({ kind: "box", ...g, y0: p.top - .4, y1: p.top, blocksShip: false });
    for (let z = p.z0 + 4; z <= p.z1; z += 8) {
      out.push({ kind: "cyl", x: p.x0 + PILE_INSET, z, r: .3, y0: WORLD.seaFloor, y1: p.top - .4, blocksShip: true });
      out.push({ kind: "cyl", x: p.x1 - PILE_INSET, z, r: .3, y0: WORLD.seaFloor, y1: p.top - .4, blocksShip: true });
    }
    for (const b of o.buildings) {
      // Outpost buildings stand on the hillside, not at sea level. The collider
      // spans the whole rendered foundation: a downhill player's head is below
      // the floor, and physics skips any shape that starts above the head.
      const seat = outpostBuildingSeat(b);
      out.push({ kind: "box", minX: b.minX, maxX: b.maxX, minZ: b.minZ, maxZ: b.maxZ, y0: seat.base, y1: seat.floor + b.h, blocksShip: false });
    }
    out.push({ kind: "box", minX: board.x - .6, maxX: board.x + .2, minZ: board.z - 1, maxZ: board.z + 1, y0: p.top, y1: p.top + 2.4, blocksShip: false });
    out.push({ kind: "box", minX: merchant.x - .6, maxX: merchant.x + .2, minZ: merchant.z - 1.2, maxZ: merchant.z + 1.2, y0: p.top, y1: p.top + 1.1, blocksShip: false });
    out.push({ kind: "box", ...o.merchantLanding, y0: p.top - .4, y1: p.top, blocksShip: false });
    out.push({ kind: "cyl", ...o.merchantStand, r: .45, y0: p.top, y1: p.top + 1.9, blocksShip: false });
  }

  for (const r of ROCKS) {
    out.push({ kind: "cyl", x: r.x, z: r.z, r: r.r, y0: WORLD.seaFloor, y1: r.h, blocksShip: true });
  }
  if (includeBarrels) for (const b of SUPPLY_BARRELS) {
    const p = supplyBarrelWorld(b.id);
    out.push({ kind: "cyl", x: p.x, z: p.z, r: 0.5, y0: p.y - 0.5, y1: p.y + 1.1, blocksShip: false });
  }

  for (const l of LANDMASSES) {
    if (l.id === "skull") out.push(...SKULL_PALM_COLLIDERS);
    for (const p of l.id === "skull" ? [] : l.trees) {
      const x = l.cx + p.x;
      const z = l.cz + p.z;
      const base = terrainHeight(x, z);
      const r = l.theme === "frost" ? .25 : l.theme === "marsh" ? .175 : .3;
      const height = l.theme === "frost" ? 5 : l.theme === "marsh" ? 4 : 6;
      out.push({ kind: "cyl", x, z, r, y0: l.theme === "frost" || l.theme === "marsh" ? base : base - 1, y1: base + height, blocksShip: false });
    }
    for (const m of l.landmarks) {
      if (l.id === "lantern" || l.id === "skull") continue;
      const x = l.cx + m.x;
      const z = l.cz + m.z;
      const base = terrainHeight(x, z);
      switch (m.kind) {
        case "lighthouse":
          // Radii match the rendered towers at walking height (12- and 8-sided, between corner and flat).
          out.push({ kind: "cyl", x, z, r: 2.75, y0: base - 1, y1: base + 18, blocksShip: false });
          break;
        case "windmill":
          out.push({ kind: "cyl", x, z, r: 2.85, y0: base - 1, y1: base + 11, blocksShip: false });
          break;
        case "hut":
          out.push({ kind: "box", minX: x - 3, maxX: x + 3, minZ: z - 2.5, maxZ: z + 2.5, y0: base - 1, y1: base + 3.5, blocksShip: false });
          break;
        case "arch":
          out.push({ kind: "box", minX: x - 6, maxX: x - 3.5, minZ: z - 1.5, maxZ: z + 1.5, y0: base - 1, y1: base + 7, blocksShip: false });
          out.push({ kind: "box", minX: x + 3.5, maxX: x + 6, minZ: z - 1.5, maxZ: z + 1.5, y0: base - 1, y1: base + 7, blocksShip: false });
          out.push({ kind: "box", minX: x - 6, maxX: x + 6, minZ: z - 1.5, maxZ: z + 1.5, y0: base + 5.2, y1: base + 7, blocksShip: false });
          break;
        case "bigRock":
          out.push({ kind: "cyl", x, z, r: 4, y0: base - 1, y1: base + 5, blocksShip: false });
          break;
        case "shipwreck":
          out.push({ kind: "box", minX: x - 2, maxX: x + 2, minZ: z - 5, maxZ: z + 5, y0: base - 1, y1: base + 1.5, blocksShip: false });
          break;
        case "obsidianSpire":
        case "iceSpire":
          out.push({ kind: "cyl", x, z, r: 2.6, y0: base, y1: base + 9, blocksShip: false });
          break;
        case "deadTree":
          out.push({ kind: "cyl", x, z, r: .4, y0: base, y1: base + 6, blocksShip: false });
          break;
        case "stiltHut":
          for (const dx of [-2.4, 2.4]) for (const dz of [-2, 2]) out.push({ kind: "cyl", x: x + dx, z: z + dz, r: .2, y0: base, y1: base + 3, blocksShip: false });
          out.push({ kind: "box", minX: x - 3, maxX: x + 3, minZ: z - 2.5, maxZ: z + 2.5, y0: base + 3, y1: base + 6.5, blocksShip: false });
          break;
        case "pillars":
          for (let i = 0; i < 6; i++) {
            const p = polar(i * TAU / 6, 5);
            out.push({ kind: "cyl", x: x + p.x, z: z + p.z, r: .5, y0: base, y1: base + 5, blocksShip: false });
          }
          break;
        case "statue":
          out.push({ kind: "box", minX: x - 1, maxX: x + 1, minZ: z - 1, maxZ: z + 1, y0: base, y1: base + 4.4, blocksShip: false });
          break;
        case "beaconTower":
        case "seaLookout":
        case "stockadeGate":
          propColliders(m.kind, { x, y: base, z, yaw: m.yaw ?? 0 }, out);
          break;
        case "volcano":
        case "pineGrove":
        case "twinPeaks":
        case "palmGrove":
          break;
      }
    }
    buildIslandStructures(l, out);
  }
  for (const solid of LANTERN_SOLIDS) out.push(solid.collider);
  for (const solid of SKULL_SOLIDS) out.push(solid.collider);
  return out;
}

// Validate only generated content, before barrels can reject their own collider.
const provisionalColliders = buildColliders(false);
function validAuthoredPoint(l: Island, p: { x: number; z: number }): boolean {
  const x = l.cx + p.x, z = l.cz + p.z;
  if (terrainHeight(x, z) <= .5) return false;
  for (const c of provisionalColliders) {
    const distance = c.kind === "cyl" ? Math.hypot(x - c.x, z - c.z) - c.r
      : Math.hypot(Math.max(c.minX - x, 0, x - c.maxX), Math.max(c.minZ - z, 0, z - c.maxZ));
    if (distance <= 1.5) return false;
  }
  return true;
}
for (const l of generatedIslands) {
  if (l.id === "skull") continue; // Routes, digs and barrel6 are authored together, never auto-relocated.
  const barrel = SUPPLY_BARRELS.find((b) => b.island === l.id)!;
  for (const p of [...l.digSpots, barrel]) {
    if (validAuthoredPoint(l, p)) continue;
    const angle = Math.atan2(p.z, p.x);
    let found = false;
    for (const fraction of [.6, .5, .4, .3]) {
      for (let k = 0; k < 24; k++) {
        const candidate = polar(angle + k * Math.PI / 12, l.radius * fraction);
        if (!validAuthoredPoint(l, candidate)) continue;
        p.x = candidate.x; p.z = candidate.z; found = true;
        break;
      }
      if (found) break;
    }
    if (!found) throw new Error(`no accessible authored point on ${l.id}`);
  }
  for (const p of l.digSpots) p.hint = compassHint(l.landmarks.find((m) => m.id === p.landmark)!, p);
  const trees = l.trees as { x: number; z: number }[];
  for (let i = trees.length - 1; i >= 0; i--) {
    const p = trees[i]!;
    if (l.digSpots.some((d) => Math.hypot(d.x - p.x, d.z - p.z) < 8)) trees.splice(i, 1);
  }
}

export interface IslandCannonMount {
  id: IslandId;
  x: number;
  y: number;
  z: number;
  heading: number;
  point: Vec3;
  station: Vec3;
}

const cannonOffsets: readonly (readonly [IslandId, number, number])[] = [
  ["lantern", 44, -49], ["crescent", -37, -46], ["twinback", -4, -77],
  ["smuggler", 61, 16], ["coral", 73, 12], ["mistral", 0, 77],
  // Skull's authored fortress replaces the old circular ruin shore; use its graded landing approach.
  ["skull", -40, -108], ["farwatch", 63, -17],
  // In Splinter Bastion's yard beside the gate, walkable from its supply barrel, firing out over the stockade.
  ["bastion", 18.3, -56.6],
];
export const ISLAND_CANNONS: readonly IslandCannonMount[] = cannonOffsets.map(([id, lx, lz]) => {
  const island = getIsland(id);
  const x = island.cx + lx, z = island.cz + lz, y = terrainHeight(x, z);
  const heading = Math.atan2(lx, lz);
  const sx = x - Math.sin(heading), sz = z - Math.cos(heading);
  return { id, x, y, z, heading, point: { x, y: y + CANNON.pivotHeight, z },
    station: { x: sx, y: terrainHeight(sx, sz), z: sz } };
});

export function getIslandCannon(id: IslandId): IslandCannonMount {
  const mount = ISLAND_CANNONS.find((c) => c.id === id);
  if (!mount) throw new Error(`bad island cannon ${id}`);
  return mount;
}

export const COLLIDERS: readonly Collider[] = [
  ...buildColliders(),
  ...ISLAND_CANNONS.map((c): Collider => ({
    kind: "box", minX: c.x - .35, maxX: c.x + .35, minZ: c.z - .35, maxZ: c.z + .35,
    y0: c.y, y1: c.y + .8, blocksShip: false,
  })),
];

/** World-space dig spot position, with y on the terrain. */
export function digSpotWorld(island: Island, spotIndex: number): { x: number; y: number; z: number } {
  const spot = island.digSpots[spotIndex];
  if (!spot) throw new Error(`bad dig spot ${island.id}#${spotIndex}`);
  const x = island.cx + spot.x;
  const z = island.cz + spot.z;
  return { x, y: terrainHeight(x, z), z };
}


export function coastPolygon(l: Landmass, segments = 64): { x: number; z: number }[] {
  const pts: { x: number; z: number }[] = [];
  for (let i = 0; i < segments; i++) {
    const theta = (i / segments) * TAU;
    const r = coastRadius(l, theta);
    pts.push({ x: Math.cos(theta) * r, z: Math.sin(theta) * r });
  }
  return pts;
}
